import React, { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import {
  Banner,
  IconButton,
  ProgressBar,
  Text,
  Tooltip,
} from 'react-native-paper'
import { useTheme } from '../../hooks/theme.hook'
import { useTranslation } from 'react-i18next'

// Export Button Component
interface ExportButtonProps {
  onPress: () => Promise<void>
  disabled?: boolean
  tooltip?: string
}

/**
 * https://www.notion.so/coderbunker/Export-Button-29c0892b6c3080199c90e84ff9d9b428?source=copy_link
 */
export function ExportButton({
  onPress,
  disabled,
  tooltip,
}: ExportButtonProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const handlePress = async () => {
    if (loading || disabled) return

    setLoading(true)
    try {
      await onPress()
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Tooltip title={tooltip || t('export-to-csv')}>
      <IconButton
        icon={loading ? 'loading' : 'download'}
        iconColor={theme.colors.primary}
        size={24}
        onPress={handlePress}
        disabled={disabled || loading}
      />
    </Tooltip>
  )
}

// Export Banner Component
interface ExportBannerProps {
  visible: boolean
  progress: number // 0-1
  message?: string
  onDismiss?: () => void
}

export function ExportBanner({
  visible,
  progress,
  message = 'Exporting data...',
  onDismiss,
}: ExportBannerProps) {
  const theme = useTheme()

  if (!visible) return null

  return (
    <View style={styles.container}>
      <Banner
        visible={visible}
        icon="download"
        style={[
          styles.banner,
          { backgroundColor: theme.colors.surfaceVariant },
        ]}
      >
        <View style={styles.content}>
          <Text variant="bodyMedium" style={styles.message}>
            {message}
          </Text>
          <ProgressBar
            progress={progress}
            style={styles.progressBar}
            color={theme.colors.primary}
          />
          <Text variant="bodySmall" style={styles.percentage}>
            {Math.round(progress * 100)}%
          </Text>
        </View>
      </Banner>
    </View>
  )
}

const styles = StyleSheet.create({
  // Banner styles
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  banner: {
    elevation: 4,
  },
  content: {
    width: '100%',
  },
  message: {
    marginBottom: 8,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: 4,
  },
  percentage: {
    textAlign: 'right',
    opacity: 0.7,
  },
})
